import { ComponentProps, ReactNode, Ref, useRef } from 'react';
import { mergeProps, useButton, useFocusRing } from 'react-aria';

import composeRefs from '@seznam/compose-react-refs';

import Badge from '~/components/scales/Badge/Badge';
import { TooltipTriggerProps, pickTooltipTriggerProps } from '~/components/scales/Tooltip';
import { validateDataCy } from '~/components/scales/utils/data-cy';

type Props = TooltipTriggerProps & {
  'aria-label': string;
  autoFocus?: boolean;
  badge?: Omit<ComponentProps<typeof Badge>, 'className'>;
  /**
   * @deprecated Avoid relying on data-cy for tests. Prefer accessible queries:
   * `screen.getByRole('button', { name: 'aria-label' })`
   */
  'data-cy'?: string;
  icon: ReactNode;
  isDisabled?: boolean;
  isPressed?: boolean;
  onClick?: () => void;
  ref?: Ref<HTMLButtonElement>;
  type?: 'default' | 'destructive';
};

export default function BabyButton(props: Props) {
  const ref = useRef<HTMLButtonElement>(null);

  const { isFocusVisible, focusProps } = useFocusRing();

  const { buttonProps } = useButton(
    {
      autoFocus: props.autoFocus,
      'aria-pressed': props.isPressed,
      'aria-label': props['aria-label'],
      isDisabled: Boolean(props.isDisabled),
      onPress: props.onClick,
    },
    ref
  );

  const classes = [
    'relative flex w-max shrink-0 items-center rounded-full p-1 justify-center transition',
  ];

  if (props.type === 'destructive') {
    classes.push(
      props.isPressed ? 'bg-button-destructive-pressed' : 'bg-button-icon',
      'text-button-destructive',
      'hover:enabled:bg-button-destructive-hover',
      isFocusVisible ? 'outline outline-button-destructive' : 'outline-none',
      'disabled:text-type-inactive disabled:border-button-inactive'
    );
  } else {
    classes.push(
      props.isPressed ? 'bg-button-icon-pressed' : 'bg-button-icon',
      'text-type-primary',
      'hover:enabled:bg-button-icon-hover',
      isFocusVisible ? 'outline' : 'outline-none',
      'disabled:text-type-inactive disabled:border-button-inactive'
    );
  }

  validateDataCy(props['data-cy'], 'button');

  return (
    <button
      {...mergeProps(pickTooltipTriggerProps(props), buttonProps, focusProps)}
      ref={composeRefs(ref, props.ref)}
      className={[...classes].join(' ')}
      data-cy={props['data-cy']}
    >
      <div className="contents [&>*]:!icon-sm">{props.icon}</div>
      {props.badge ? <Badge {...props.badge} className="absolute -right-3 -top-2" /> : null}
    </button>
  );
}
